/**
 * Helper functions for product selection and dependency management
 */

/**
 * Checks if a product has color options
 */
export function hasColorOptions(materialSchema: any[], productCategory: string, productName: string): boolean {
    const categorySchema = materialSchema.find((schemaItem: any) => schemaItem.Name === productCategory);
    const selectedProductSubcategories =
        categorySchema?.Subcategories?.filter((sub: any) => sub.Name === productName) || [];
    return selectedProductSubcategories.some((sub: any) => sub.Color);
}

/**
 * Finds manufacturerProductId from materialSchema for a given category and product name
 */
export function findManufacturerProductId(
    materialSchema: any[],
    category: string,
    productName: string
): string | undefined {
    if (!materialSchema || !Array.isArray(materialSchema)) {
        return undefined;
    }

    const categorySchema = materialSchema.find((schemaItem: any) => {
        const name = schemaItem.Name || schemaItem.ProductName || schemaItem.name;
        return name === category;
    });

    if (!categorySchema || !categorySchema.Subcategories) {
        return undefined;
    }

    // Find the first subcategory that matches the product name
    // If there are multiple, prefer one without color (default option)
    const matchingSubs = categorySchema.Subcategories.filter((sub: any) => sub.Name === productName);

    if (matchingSubs.length === 0) {
        return undefined;
    }

    // Prefer subcategory without color (default), otherwise return first match
    const defaultSub = matchingSubs.find((sub: any) => !sub.Color);
    return defaultSub?.Id || matchingSubs[0]?.Id;
}

/**
 * Recursively sets dependencies for a selected product
 */
export function setDependencies(
    dependencies: any[],
    tempStateData: any,
    category: string,
    product: string,
    materialSchema?: any[]
): void {
    const deps =
        dependencies
            .find((dep: any) => dep.Category === category)
            ?.Mappings?.find((map: any) => map.Product === product)?.RequiredProducts || [];

    for (const dep of deps) {
        // Look up manufacturerProductId from materialSchema if available
        let manufacturerProductId: string | undefined;
        if (materialSchema) {
            manufacturerProductId = findManufacturerProductId(materialSchema, dep.Category, dep.Product);
        }

        tempStateData[dep.Category] = {
            quantity: 1,
            value: dep.Product,
            manufacturerProductId,
        };
        // Recursively set dependencies, passing materialSchema
        setDependencies(dependencies, tempStateData, dep.Category, dep.Product, materialSchema);
    }
}

/**
 * Derives color from materialSchema based on ManufacturerProductId
 */
export function deriveColorFromSchema(
    materialSchema: any[],
    manufacturerProductId: string | null | undefined,
    productCategory: string
): string | undefined {
    if (!manufacturerProductId || !Array.isArray(materialSchema)) {
        return undefined;
    }

    const categoryEntry = materialSchema.find((prod: any) => {
        const name = prod.Name || prod.ProductName || prod.name;
        return name === productCategory;
    });

    const sub = categoryEntry?.Subcategories?.find((s: any) => s.Id === manufacturerProductId && s.Color);
    return sub?.Color as string | undefined;
}
